import React from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { FaqArticle, FaqCategory, HELP_LIST_TRACK_CAT, hidden } from '../util';
import { FaqCategoryView } from './HelpListCategory';
import { HelpListSideMenu } from './HelpListSideMenu';
import {
  Type,
  screenMdMax,
  screenSmMax,
  screenSmMin,
  screenXsMax,
  screenXsMin,
  spacer24,
  spacer32,
  spacer48,
} from '@spotify-internal/encore-web';
import { FaqArticleView } from './HelpListArticle';
import { ButtonSecondaryAsLink } from '../../ui';
import { getBurst } from '../../utils';
import { contentPadding } from './common';
import { itemToSearchHit } from '../../Search';
import { FAQItem, Variant } from '../HelpFAQ/FAQItem';
import { useLocale, useT } from '@spotify-internal/i18n-core';

const Container = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;

  // most modules display under the navbar, this one needs to be below it
  min-height: calc(100vh - 76px);
  margin-top: 10px;

  @media (max-width: ${screenSmMax}) {
    min-height: calc(100vh - 68px);
    margin-top: 2px;
  }

  @media (max-width: ${screenSmMin}) {
    display: block;
  }
`;

const SideMenuContainer = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  max-width: 350px;
  padding: ${spacer32} ${spacer24};

  color: ${({ theme }) => theme.help.sideMenu.text};
  background: ${({ theme }) => theme.help.sideMenu.background};

  @media (max-width: ${screenSmMin}) {
    ${hidden}
  }
`;

const ContentContainer = styled.div`
  flex: 2;
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  overflow-x: hidden;
  background: ${({ theme }) => theme.box.background};
  color: ${({ theme }) => theme.box.text};

  @media (max-width: ${screenSmMin}) {
    min-height: calc(100vh - 50px);
  }

  @media (max-width: ${screenXsMin}) {
    min-height: calc(100vh - ${spacer48});
  }
`;

const Banner = styled.div`
  flex: 1;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-height: 250px;
  min-height: 221px;
  ${contentPadding};
  background-color: ${({ theme }) => theme.help.banner.background};
  color: ${({ theme }) => theme.help.banner.text};
  ${({ theme }) => getBurst(theme.help.banner.text)}
  background-position: right center;
  background-repeat: no-repeat;
  background-size: contain;

  @media (max-width: ${screenMdMax}) {
    background-image: none;
    flex-flow: column;
    justify-content: space-evenly;
  }
`;

const BannerText = styled(Type.h2).attrs({
  paddingBottom: '0.75em',
  variant: 'alto',
  weight: 'book',
})`
  padding-bottom: 0px;
  line-height: 0;
  @media (max-width: ${screenMdMax}) {
    line-height: revert;
  }
`;

const ContactUsButton = styled(ButtonSecondaryAsLink)`
  background-color: ${({ theme }) => theme.help.banner.text};
  color: ${({ theme }) => theme.help.banner.ctaText};
  border: 0;
  white-space: nowrap;

  &&:hover:not(:disabled),
  &:focus:not(:disabled),
  &:active:not(:disabled) {
    color: ${({ theme }) => theme.help.banner.background};
    border: 0;
  }

  @media (max-width: ${screenSmMax}) {
    width: 100%;
  }
`;

const RelatedContent = styled.div`
  flex: 1;
  width: 100%;
  ${contentPadding};
  background-color: ${({ theme }) => theme.help.grayBox.background};

  @media (max-width: ${screenXsMax}) {
    flex: 0;
  }
`;

const RelatedContentTitle = styled(Type.h3).attrs({
  variant: 'celloCanon',
  weight: 'bold',
})`
  font-size: ${spacer32};
`;

export type HelpListType = {
  faqCategories: FaqCategory[];
  currentCategory?: FaqCategory;
  currentArticle?: FaqArticle;
};
export const HelpList: React.FC<HelpListType> = template => {
  const t = useT();
  const locale = useLocale();
  const router = useRouter();
  const { faqCategories, currentCategory, currentArticle } = template;
  const related = currentArticle?.related
    ?.map(item => itemToSearchHit(item, locale))
    .filter(Boolean)
    .map(hit => (
      <React.Fragment key={hit!.url}>
        <FAQItem hit={hit!} variant={Variant.RELATED} />
      </React.Fragment>
    ));

  return (
    <Container>
      <SideMenuContainer data-testid="help-list-nav">
        <HelpListSideMenu
          categories={faqCategories}
          currentCategory={currentCategory}
        />
      </SideMenuContainer>
      <ContentContainer data-testid="help-list-content">
        {currentArticle ? (
          <>
            <FaqArticleView
              article={currentArticle}
              category={currentCategory}
            />
            {related && related.length > 0 ? (
              <RelatedContent>
                <RelatedContentTitle>
                  {t(
                    'FRODOR_HELP_LIST_RELATED_CONTENT',
                    'Related content',
                    'Content that is related to the current article',
                  )}
                </RelatedContentTitle>
                {related}
              </RelatedContent>
            ) : null}
          </>
        ) : (
          <FaqCategoryView category={currentCategory} />
        )}
        <Banner data-testid="help-list-banner">
          <BannerText>
            {t(
              'FRODOR_HELP_LIST_CANT_FIND',
              "Can't find what you're looking for?",
              '',
            )}
          </BannerText>
          <ContactUsButton
            href={`${router.basePath}/contact`}
            forceLoad
            track={[HELP_LIST_TRACK_CAT, 'Contact us']}
          >
            {t('FRODOR_HELP_LIST_CONTACT_US', 'Contact us', '')}
          </ContactUsButton>
        </Banner>
      </ContentContainer>
    </Container>
  );
};
